<template>
  <!-- Étapes d'activation 2FA (choix de la méthode → vérification → codes de
       secours). Partagées par le popup MandatoryTwoFAModal (profil, rappel) et
       par la page plein écran /pages/2fa-setup (activation forcée au login,
       token de configuration seul). N'appelle que /auth/2fa/setup/* . -->
  <div class="twofa-setup-steps">
    <!-- STEP 1 — choose method -->
    <div v-if="step === 'choose'">
      <h5 class="mb-3">Choose your second factor</h5>
      <p class="text-sm mb-3">
        Pick the method that fits how you work. You can change it later from
        <em>Configurations &gt; Edit Profile &gt; Security</em>.
      </p>
      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <div class="border rounded p-4 cursor-pointer h-full" @click="chooseTotp">
            <h6><feather-icon icon="SmartphoneIcon" class="mr-1" /> Authenticator app</h6>
            <p class="text-sm mt-2">Use Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc. Recommended — works without internet, no email delays.</p>
            <vs-button color="primary" class="mt-2" @click.stop="chooseTotp">Use authenticator app</vs-button>
          </div>
        </div>
        <div class="vx-col w-full md:w-1/2 mb-4">
          <div class="border rounded p-4 cursor-pointer h-full" @click="chooseEmail">
            <h6><feather-icon icon="MailIcon" class="mr-1" /> Email code</h6>
            <p class="text-sm mt-2">A 6-digit code is sent to your email at every sign-in. Simpler, but slower and depends on your inbox being reachable.</p>
            <vs-button color="primary" class="mt-2" @click.stop="chooseEmail">Use email codes</vs-button>
          </div>
        </div>
      </div>
      <vs-alert v-if="error" color="danger" class="mt-3">{{ error }}</vs-alert>
    </div>

    <!-- STEP 2A — TOTP setup (QR + verify) -->
    <div v-else-if="step === 'totp-scan'">
      <h5 class="mb-3">Scan this QR code</h5>
      <p class="text-sm mb-3">
        Open your authenticator app, scan the code, then enter the 6-digit code it displays to confirm.
      </p>
      <div v-if="qrDataUrl" class="text-center my-4">
        <img :src="qrDataUrl" alt="2FA QR code" style="max-width: 220px;" />
      </div>
      <p class="text-xs mt-2 text-grey" v-if="totpSecret">
        Cannot scan? Enter this key manually: <code>{{ totpSecret }}</code>
      </p>
      <vs-input
        v-model="code"
        label-placeholder="6-digit code"
        icon-pack="feather" icon="icon-key" icon-no-border
        class="w-full mt-4" :maxlength="6"
      />
      <vs-alert v-if="error" color="danger" class="mt-3">{{ error }}</vs-alert>
      <div class="flex justify-end mt-4">
        <vs-button type="border" class="mr-2" @click="step = 'choose'">Back</vs-button>
        <vs-button color="success" :disabled="!/^\d{6}$/.test(code)" @click="verifyTotp">Verify and enable</vs-button>
      </div>
    </div>

    <!-- STEP 2B — Email setup (enter code) -->
    <div v-else-if="step === 'email-verify'">
      <h5 class="mb-3">Enter the email code</h5>
      <p class="text-sm mb-3">
        We sent a 6-digit code to <strong>{{ email }}</strong>. The code expires in 10 minutes.
      </p>
      <vs-input
        v-model="code"
        label-placeholder="6-digit code"
        icon-pack="feather" icon="icon-mail" icon-no-border
        class="w-full" :maxlength="6"
      />
      <vs-alert v-if="error" color="danger" class="mt-3">{{ error }}</vs-alert>
      <div class="flex justify-end mt-4">
        <vs-button type="border" class="mr-2" @click="step = 'choose'">Back</vs-button>
        <vs-button type="flat" class="mr-2" @click="resendEmail">Resend code</vs-button>
        <vs-button color="success" :disabled="!/^\d{6}$/.test(code)" @click="verifyEmail">Verify and enable</vs-button>
      </div>
    </div>

    <!-- STEP 3 — recovery codes (shown once) -->
    <div v-else-if="step === 'recovery'">
      <h5 class="mb-3">Save your recovery codes</h5>
      <vs-alert color="warning" icon-pack="feather" icon="icon-alert-triangle" :active="true" class="mb-3">
        Each of these 10 codes can be used <strong>once</strong> if you lose access to your second factor. Save them somewhere safe right now — we won't show them again.
      </vs-alert>
      <pre class="recovery-block">{{ recoveryCodes.join('\n') }}</pre>
      <div class="flex mt-3">
        <vs-button type="border" icon-pack="feather" icon="icon-copy" @click="copyCodes" class="mr-2">Copy</vs-button>
        <vs-button type="border" icon-pack="feather" icon="icon-download" @click="downloadCodes">Download .txt</vs-button>
      </div>
      <div class="mt-4">
        <vs-checkbox v-model="acknowledged">I have saved my recovery codes in a safe place</vs-checkbox>
      </div>
      <div class="flex justify-end mt-4">
        <vs-button color="success" :disabled="!acknowledged" @click="finish">Done</vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import twofa from '@/http/requests/auth/twofa.js'

export default {
  name: 'TwoFASetupSteps',
  data () {
    return {
      // choose | totp-scan | email-verify | recovery
      step: 'choose',
      code: '',
      totpSecret: null,
      qrDataUrl: null,
      recoveryCodes: [],
      acknowledged: false,
      error: null,
      email: localStorage.getItem('login-email') || ''
    }
  },
  methods: {
    errorMessage (err, fallback) {
      return (err && err.response && err.response.data && err.response.data.message) || fallback
    },
    // Token de configuration périmé (15 min) ou session perdue : l'intercepteur
    // axios a déjà purgé le localStorage ; le parent décide quoi faire.
    handleAuthError (err) {
      if (err && err.response && err.response.status === 401) {
        this.$emit('session-expired')
        return true
      }
      return false
    },
    chooseTotp () {
      this.error = null
      this.code = ''
      this.$vs.loading()
      twofa.setupTotpStart()
        .then(res => {
          this.$vs.loading.close()
          this.totpSecret = res.data.secret
          this.qrDataUrl  = res.data.qrDataUrl
          this.step = 'totp-scan'
        })
        .catch(err => {
          this.$vs.loading.close()
          if (this.handleAuthError(err)) return
          this.error = this.errorMessage(err, 'Could not start TOTP setup.')
        })
    },
    chooseEmail () {
      this.error = null
      this.code = ''
      this.$vs.loading()
      twofa.setupEmailStart()
        .then(() => {
          this.$vs.loading.close()
          this.step = 'email-verify'
        })
        .catch(err => {
          this.$vs.loading.close()
          if (this.handleAuthError(err)) return
          this.error = this.errorMessage(err, 'Could not send email code.')
        })
    },
    resendEmail () { this.chooseEmail() },
    verifyTotp () {
      this.runVerify(twofa.setupTotpVerify(this.code))
    },
    verifyEmail () {
      this.runVerify(twofa.setupEmailVerify(this.code))
    },
    runVerify (promise) {
      this.error = null
      this.$vs.loading()
      promise
        .then(res => {
          this.$vs.loading.close()
          // The activation response carries the full auth payload (id, roles,
          // accessToken, …) + recoveryCodes. Replace the now-stale token
          // (pre-2fa-setup or old full token) with the fresh full one.
          if (res.data.accessToken) localStorage.setItem('accessToken', res.data.accessToken)
          this.recoveryCodes = res.data.recoveryCodes || []
          this.step = 'recovery'
          localStorage.setItem('xylo:twofa-status', JSON.stringify({
            twofa_enabled: true, twofa_method: res.data.twofa_method
          }))
          this.$emit('activated', res.data)
        })
        .catch(err => {
          this.$vs.loading.close()
          if (this.handleAuthError(err)) return
          this.error = this.errorMessage(err, 'Verification failed.')
        })
    },
    copyCodes () {
      try {
        navigator.clipboard.writeText(this.recoveryCodes.join('\n'))
        this.$vs.notify({ title: 'Copied', text: 'Recovery codes copied to clipboard.', color: 'success', position: 'bottom-right' })
      } catch (e) {
        this.$vs.notify({ title: 'Copy failed', text: 'Please select and copy the codes manually.', color: 'warning', position: 'bottom-right' })
      }
    },
    downloadCodes () {
      const blob = new Blob([`${this.recoveryCodes.join('\n')  }\n`], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'xylo-recovery-codes.txt'
      a.click()
      URL.revokeObjectURL(url)
    },
    finish () {
      this.$emit('done')
    }
  }
}
</script>

<style scoped>
.recovery-block {
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px 16px;
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  letter-spacing: 0.5px;
  margin: 0;
  white-space: pre-wrap;
}
</style>
